import { idbGet, idbSet, idbDelete, idbKeysByPrefix } from './idb';
import { createLogger } from '@/lib/logger';
import { isEffectivelyOffline } from '@/lib/offlineFlag';

const log = createLogger('PersistedCache');

const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes
const KEY_PREFIX = 'cache:';

export interface CacheEntry<T = Record<string, unknown>> {
  data: T[];
  timestamp: number;
  ttl: number;
}

export interface Row {
  id: string;
  [key: string]: unknown;
}

function cacheKey(schema: string, table: string): string {
  return `${KEY_PREFIX}${schema}:${table}`;
}

export async function cacheGet<T extends Row>(
  schema: string,
  table: string
): Promise<T[] | undefined> {
  const key = cacheKey(schema, table);
  const entry = await idbGet<CacheEntry<T>>(key);

  if (!entry) {
    return undefined;
  }

  const now = Date.now();
  const expired = now - entry.timestamp > entry.ttl;

  if (expired) {
    // ОФЛАЙН (в т.ч. «Автономный режим»): срок давности НЕ отменяет данные —
    // сохранённое отдаётся (сервер обновит при связи); иначе через 5 минут
    // PWA «теряла» всё скачанное. В сети — как раньше (данные устарели).
    if (!isEffectivelyOffline()) {
      log.debug(`Cache expired for ${schema}.${table}`);
      return undefined;
    }
  }

  return entry.data;
}

export async function cacheSet<T extends Row>(
  schema: string,
  table: string,
  data: T[],
  ttl: number = DEFAULT_TTL_MS
): Promise<void> {
  const key = cacheKey(schema, table);
  const entry: CacheEntry<T> = {
    data,
    timestamp: Date.now(),
    ttl,
  };
  await idbSet(key, entry);
}

/**
 * Merge new rows into existing cache by id.
 * This is critical for sync pull: pulled records update existing rows,
 * other rows remain untouched.
 */
export async function cacheMerge<T extends Row>(
  schema: string,
  table: string,
  rows: T[],
  ttl: number = DEFAULT_TTL_MS
): Promise<void> {
  const existing = (await cacheGet<T>(schema, table)) ?? [];
  const byId = new Map<string, T>();

  for (const row of existing) {
    if (row.id) {
      byId.set(row.id, row);
    }
  }

  for (const row of rows) {
    if (row.id) {
      byId.set(row.id, row);
    }
  }

  await cacheSet(schema, table, Array.from(byId.values()), ttl);
}

export async function cacheGetById<T extends Row>(
  schema: string,
  table: string,
  id: string
): Promise<T | undefined> {
  const rows = await cacheGet<T>(schema, table);
  if (!rows) return undefined;
  return rows.find(r => r.id === id);
}

export async function cacheRemove<T extends Row>(
  schema: string,
  table: string,
  id: string
): Promise<void> {
  const rows = (await cacheGet<T>(schema, table)) ?? [];
  const filtered = rows.filter(r => r.id !== id);
  await cacheSet(schema, table, filtered, DEFAULT_TTL_MS);
}

export async function cacheClear(schema: string, table: string): Promise<void> {
  await idbDelete(cacheKey(schema, table));
}

export async function cacheClearAll(): Promise<void> {
  const keys = await idbKeysByPrefix(KEY_PREFIX);
  for (const key of keys) {
    await idbDelete(key);
  }
}

export async function cacheKeys(): Promise<string[]> {
  return idbKeysByPrefix(KEY_PREFIX);
}

/** Время сохранения кэша таблицы (для баннера «данные из кэша»). */
export async function cacheGetSavedAt(schema: string, table: string): Promise<number | undefined> {
  const entry = await idbGet<CacheEntry>(cacheKey(schema, table));
  return entry?.timestamp;
}
